import { CodedProperty, NamedProperty, ResolutionProperty } from "sprint-common-web";

import {
    getOnPremMovieDetailsHref,
    getOnPremTvSeriesDetailsHref,
    getOnPremAncillaryDetailsHref
} from "../../../../assets/AssetsRoutes";
import { NoteItem } from "../../../../common/components/notes/hooks/state/NoteItem";
import { ContentReferenceType } from "../../../../common/types/ContentReferenceType";
import { DynamicSubtitleType } from "../../../../common/types/DynamicSubtitleType";
import { getLanguageDisplayName } from "../../../../common/types/Language";
import { OrderContentReference } from "../../../../common/types/OrderContentReference";
import { RecordType } from "../../../../common/types/RecordType";
import { OrderAssetAvailability } from "../../../common/types/OrderAssetAvailability";
import { OnPremOrderDashboardItem } from "../OnPremOrderDashboardItem";

export type OnPremOrderAssetDashboardItem =
    | OnPremOrderAssetVideoDashboardItem
    | OnPremOrderAssetSubtitleDashboardItem
    | OnPremOrderAssetSoundtrackDashboardItem;

export interface OnPremOrderAssetDashboardItemBase {
    recordType: RecordType;
    name: string;
    filePath: string;
    isTrailer?: boolean;
    assetHref?: string;
    assetId?: string;
    availability: OrderAssetAvailability;
    notes: NoteItem[];
}

export interface OnPremOrderAssetVideoDashboardItem extends OnPremOrderAssetDashboardItemBase {
    recordType: RecordType.Video;
    details: {
        subtitle: CodedProperty;
        aspectRatio: NamedProperty;
        sourceResolution?: ResolutionProperty;
        targetResolution?: ResolutionProperty;
        nonMatchingVideos: {
            assetId: string;
            subtitle: CodedProperty;
            aspectRatio: NamedProperty;
            resolution: ResolutionProperty;
        }[];
    };
}

export interface OnPremOrderAssetSubtitleDashboardItem extends OnPremOrderAssetDashboardItemBase {
    recordType: RecordType.Subtitle;
    subtitleType: DynamicSubtitleType | undefined;
    details: {
        language: CodedProperty;
    };
}

export interface OnPremOrderAssetSoundtrackDashboardItem extends OnPremOrderAssetDashboardItemBase {
    recordType: RecordType.Audio;
    details: {
        language: CodedProperty;
    };
}

export const mapToOnPremOrderDaschboardAssetsItems = (
    orderDashboardItem: OnPremOrderDashboardItem
): OnPremOrderAssetDashboardItem[] => {
    const video: OnPremOrderAssetDashboardItem = {
        name: orderDashboardItem.contentTitle ?? "-",
        filePath: orderDashboardItem.onPremAssets.video?.filePath ?? "-",
        recordType: RecordType.Video,
        availability: orderDashboardItem.onPremAssets.video.availability,
        assetHref: getAssetHref(orderDashboardItem.content, orderDashboardItem.onPremAssets.video?.assetId),
        assetId: orderDashboardItem.onPremAssets.video?.assetId,
        details: {
            targetResolution: orderDashboardItem.targetResolution,
            aspectRatio: orderDashboardItem.onPremAssets.video.aspectRatio,
            sourceResolution: orderDashboardItem.onPremAssets.video.sourceResolution,
            subtitle: orderDashboardItem.onPremAssets.video.subtitle,
            nonMatchingVideos: orderDashboardItem.onPremAssets.video.nonMatchingVideos.map((nmv) => ({
                assetId: nmv.assetId,
                subtitle: nmv.subtitle,
                aspectRatio: nmv.aspectRatio,
                resolution: nmv.resolution
            }))
        },
        notes: orderDashboardItem.assetNotes.filter(
            (note) => note.subjectId === orderDashboardItem.onPremAssets.video.assetId
        )
    };

    const soundtracks: OnPremOrderAssetDashboardItem[] = orderDashboardItem.onPremAssets.soundtracks.map(
        (soundtrack) => {
            return {
                name: getLanguageDisplayName(soundtrack.language),
                availability: soundtrack.availability,
                filePath: soundtrack?.filePath ?? "-",
                recordType: RecordType.Audio,
                assetHref: getAssetHref(orderDashboardItem.content, soundtrack?.assetId),
                details: {
                    language: soundtrack.language
                },
                notes: orderDashboardItem.assetNotes.filter((note) => note.subjectId === soundtrack.assetId),
                assetId: soundtrack.assetId
            };
        }
    );

    const subtitles: OnPremOrderAssetDashboardItem[] = orderDashboardItem.onPremAssets.subtitles.map((subtitle) => {
        return {
            name: getLanguageDisplayName(subtitle.language),
            availability: subtitle.availability,
            filePath: subtitle?.filePath ?? "-",
            recordType: RecordType.Subtitle,
            subtitleType: subtitle.subtitleType,
            assetHref: getAssetHref(orderDashboardItem.content, subtitle?.assetId),
            details: {
                language: subtitle.language
            },
            notes: orderDashboardItem.assetNotes.filter((note) => note.subjectId === subtitle.assetId),
            assetId: subtitle.assetId
        };
    });

    return [video, ...soundtracks, ...subtitles];
};

const getAssetHref = (content: OrderContentReference, assetId: string | undefined) => {
    if (!assetId) {
        return undefined;
    }

    switch (content.type) {
        case ContentReferenceType.Movie:
            return getOnPremMovieDetailsHref(assetId, content.subtype);

        case ContentReferenceType.TvSeries:
            return getOnPremTvSeriesDetailsHref(assetId);

        case ContentReferenceType.Ancillary:
            return getOnPremAncillaryDetailsHref(assetId, content.subtype);

        default:
            throw new Error("Unsupported content reference type");
    }
};
